import {
  Box,
  Collapse,
  Drawer as MuiDrawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  useMediaQuery,
  useTheme,
  Tooltip,
  SxProps,
  Theme,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  BarChart,
  ChevronLeft,
  ChevronRight,
  CurrencyExchange,
  ExpandLess,
  ExpandMore,
  Feed,
  Home,
  MonetizationOn,
  Moving,
  Toll,
} from '@mui/icons-material';
import { useHistory, useLocation } from 'react-router';
import { MouseEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  assetAllocationUrl,
  allTransactionsUrl,
  beneficiariesUrl,
  benefitStatementUrl,
  contributionsUrl,
  documentsUrl,
  holdingsUrl,
  homeUrl,
  insuranceUrl,
  pensionUrl,
  performanceUrl,
} from '@src/app/utils/UrlBuilder';
import { useAppSelector } from '@src/app/hooks/store';
import { SupersimplifierStore, selectSupersimplifier } from '@src/app/store/reducers/supersimplifier';
import { useAppContext } from '@src/app/hooks/useAppContext';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { ScreenState } from '@src/app/enums';
import { BenchmarkInfo } from 'clientportalshared';

const DRAWER_WIDTH = 285;

interface DrawerRouteList {
  routes: DrawerRoute[];
  subMenu: boolean;
}

interface DrawerRoute {
  icon?: ReactNode;
  label: string;
  path?: string;
  children?: DrawerRoute[];
  sub?: boolean;
}

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  ...theme.mixins.toolbar,
}));

const DrawerList = (props: DrawerRouteList) => {
  const { drawerExpanded } = useAppContext();
  const sx: SxProps<Theme> = !drawerExpanded ? { display: 'flex', flexDirection: 'column', alignItems: 'center' } : {};
  return (
    <List sx={sx}>
      {props.routes.map((route, index) => (
        <DrawerItem sub={props.subMenu} key={index} {...route} />
      ))}
    </List>
  );
};

const DrawerItem = (props: DrawerRoute) => {
  const { drawerExpanded, setDrawerExpanded, transitioningMenu } = useAppContext();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const selected = props.path && location.pathname.includes(props.path);
  const sidePadding = props.sub ? 2 : 1;

  const isSubMenuOpened = (): boolean => {
    if (!props.children) {
      return false;
    }
    return props.children.findIndex((child) => child.path && location.pathname.includes(child.path)) !== -1;
  };

  const [expanded, setExpanded] = useState(isSubMenuOpened());

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleListButtonClick = () => {
    if (props.path) {
      if (isMd) {
        setDrawerExpanded(false);
      }
      history.push(props.path);
    } else {
      setExpanded(!expanded);
    }
  };

  const handleIconButtonClick = (target: MouseEvent<HTMLElement>) => {
    if (props.path) {
      history.push(props.path);
    } else {
      setExpanded(!expanded);
      setAnchorEl(target.currentTarget);
    }
  };

  const handleMenuRouteClick = (route: DrawerRoute) => {
    if (route.path) {
      history.push(route.path);
    }
    handleClose();
  };

  if (!drawerExpanded) {
    return (
      <>
        <Box pb={1} height="56px" justifyContent="center" display="flex">
          <Tooltip title={props.label}>
            <IconButton size="large" onClick={(e) => handleIconButtonClick(e)} disabled={props.path === '#'} sx={{ color: '#222222' }}>
              {props.icon}
            </IconButton>
          </Tooltip>
        </Box>
        {props.children && (
          <Menu onClose={handleClose} anchorEl={anchorEl} open={Boolean(anchorEl)}>
            {props.children?.map((child, index) => (
              <MenuItem onClick={() => handleMenuRouteClick(child)} key={index}>
                {child.label}
              </MenuItem>
            ))}
          </Menu>
        )}
      </>
    );
  }

  return (
    <Box pl={sidePadding} pr={sidePadding} pb={1} sx={{ overflowX: 'hidden' }}>
      <ListItemButton
        disabled={props.path === '#'}
        onClick={handleListButtonClick}
        sx={{ borderRadius: '8px', background: selected ? '#F4F4F4' : 'inherit' }}
      >
        {props.icon && <ListItemIcon sx={{ color: '#222222' }}>{props.icon}</ListItemIcon>}
        {!transitioningMenu && (
          <>
            <ListItemText
              primaryTypographyProps={{
                color: '#222222',
                textOverflow: 'clip',
                fontSize: '16px',
              }}
              primary={props.label}
            />
            {props.children && <>{expanded ? <ExpandLess sx={{ color: '#222222' }} /> : <ExpandMore sx={{ color: '#222222' }} />}</>}
          </>
        )}
      </ListItemButton>

      {!transitioningMenu && props.children && (
        <Box pl={6}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <DrawerList subMenu={true} routes={props.children} />
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

const Drawer = ({ children }: { children: ReactNode }): JSX.Element => {
  const { drawerExpanded, setDrawerExpanded, setTransitioningMenu } = useAppContext();
  const { logoUrl, portfolioInfo, portfolioInfoState } = useAppSelector(selectPortfolios);

  const [triggeredCollapse, setTriggeredCollapse] = useState<boolean>(false);

  const superSimplifierStore: SupersimplifierStore = useAppSelector(selectSupersimplifier);

  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const isBelowHd = useMediaQuery(theme.breakpoints.down(1280));

  const drawerWidth = isXs ? DRAWER_WIDTH - 15 : isMd ? DRAWER_WIDTH : drawerExpanded ? DRAWER_WIDTH : 74;

  const isOpen = !isMd || drawerExpanded;

  const routes: DrawerRoute[] = useMemo(() => {
    const list: DrawerRoute[] = [];
    list.push({ icon: <Home />, label: 'Dashboard', path: homeUrl() });
    list.push({ icon: <BarChart />, label: 'Holdings', path: holdingsUrl() });
    list.push({ icon: <MonetizationOn />, label: 'Asset Allocation', path: assetAllocationUrl() });

    if (portfolioInfoState === ScreenState.SuccessDataLoaded) {
      const benchmarks: BenchmarkInfo[] = portfolioInfo.benchMarkInfoList || [];
      const supressPerformance = benchmarks.find((benchmark) => benchmark.name?.startsWith('Suppress Performance'));
      if (!supressPerformance) {
        list.push({ icon: <Moving />, label: 'Performance', path: performanceUrl() });
      }
    }

    list.push({ icon: <CurrencyExchange />, label: 'Transactions', path: allTransactionsUrl() });
    list.push({ icon: <Feed />, label: 'Documents', path: documentsUrl() });

    const accounts = superSimplifierStore.superSimplifierAccounts;
    if (accounts?.data?.length) {
      const subMenuSuper: DrawerRoute = {
        label: 'Super Simplifier',
        icon: <Toll />,
        children: [],
      };

      const isPensionAccount = accounts.data.some((i) => i.type === 'Pension');
      const isSuperAccount = accounts.data.some((i) => i.type === 'Super');
      subMenuSuper.children?.push({ label: 'Benefit Statement', path: benefitStatementUrl() });
      subMenuSuper.children?.push({ label: 'Beneficiaries', path: beneficiariesUrl() });

      if (isPensionAccount) {
        subMenuSuper.children?.push({ label: 'Pension Details', path: pensionUrl() });
      }
      subMenuSuper.children?.push({ label: 'Insurance', path: insuranceUrl() });

      if (isSuperAccount) {
        subMenuSuper.children?.push({ label: 'Contributions', path: contributionsUrl() });
      }
      list.push(subMenuSuper);
    }

    return list;
  }, [portfolioInfoState, portfolioInfo, superSimplifierStore.superSimplifierAccounts]);

  const toggle = () => {
    if (isMd) {
      setDrawerExpanded(!drawerExpanded);
    }
  };

  useEffect(() => {
    if (isBelowHd) {
      if (!triggeredCollapse) {
        setDrawerExpanded(false);
        setTriggeredCollapse(true);
      }
    } else {
      setTriggeredCollapse(false);
    }
  }, [isBelowHd]);

  return (
    <Box component="div" display={!isMd ? 'flex' : ''}>
      <MuiDrawer
        variant={isMd ? 'temporary' : 'permanent'}
        open={isOpen}
        onClose={toggle}
        onTransitionEnd={() => {
          setTransitioningMenu(false);
        }}
        sx={{
          flexShrink: 0,
          width: drawerWidth,
          overflowX: 'hidden',

          [`& .MuiDrawer-paper`]: {
            backgroundColor: '#FFFFFF',
            borderRight: !isMd ? '1px solid #E9E9E9' : '',
            position: 'relative',
            minHeight: !isMd ? 'calc(100vh - 203px)' : 'auto',
            width: drawerWidth,
            margin: 0,
            overflowX: 'hidden',
            transition: !isOpen
              ? theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.leavingScreen,
                })
              : theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
          },
        }}
      >
        <>
          {isMd && (
            <DrawerHeader>
              <Box display="flex" flex={1}>
                {logoUrl && (
                  <Link to={homeUrl()} color="inherit">
                    <img src={logoUrl} alt="Logo" width="164" />
                  </Link>
                )}
              </Box>

              <IconButton size="large" onClick={() => setDrawerExpanded(!drawerExpanded)}>
                {drawerExpanded ? <ChevronLeft /> : <ChevronRight />}
              </IconButton>
            </DrawerHeader>
          )}
          <Box sx={{ overflow: 'auto' }} marginTop={1} display="flex" flexDirection="column" p={1}>
            <DrawerList subMenu={false} routes={routes} />
          </Box>
        </>
      </MuiDrawer>
      <Box display="flex" flexDirection="column" flex={1}>
        {children}
      </Box>
    </Box>
  );
};

export default Drawer;
